import {ReactNode, useCallback, useContext, useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {css, view} from '@sail/ui/deprecated';
import {ConfigContext} from '../../../Providers/ConfigProvider';
import {UploadFileControl} from './UploadFileControl';
import {Attachment} from './Attachment';
import {CloseButton} from '../CloseButton';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {useProfile} from '../../../Providers/ProfileProvider';

export const messages = defineMessages({
  fileTooBigNotice: {
    id: 'support_conversations_client.attachments.fileTooBigNotice',
    description: 'Message indicating that the selected file is too big.',
    defaultMessage:
      'File is too large, please upload a file smaller than 20 MB',
  },
  fileInvalidNotice: {
    id: 'support_conversations_client.attachments.fileInvalidNotice',
    description:
      'Message indicating that the selected file is invalid or corrupted.',
    defaultMessage:
      'There appears to be an issue with the selected file, please check it and try again',
  },
  fileTypeNotSupportedNotice: {
    id: 'support_conversations_client.attachments.fileTypeNotSupportedNotice',
    description: 'Message indicating that this type of file is not supported.',
    defaultMessage:
      'File type is not supported, please upload a jpeg, png, pdf, xml, csv, tsv, xlsx, or docx file',
  },
  tooManyFilesNotice: {
    id: 'support_conversations_client.attachments.tooManyFilesNotice',
    description:
      'Message indicating that the user tried to upload too many files.',
    defaultMessage: 'A maximum of 5 files can be uploaded',
  },
  unexpectedErrorNotice: {
    id: 'support_conversations_client.attachments.unexpectedErrorNotice',
    description:
      'Message indicating that the file failed to upload for unknown reasons.',
    defaultMessage: 'File could not be uploaded, please try again later',
  },
  batchUploadErrorNotice: {
    id: 'support_conversations_client.attachments.batchUploadErrorNotice',
    description: 'Message indicating that some files failed to upload.',
    defaultMessage:
      'There was an error uploading one of your files: {errorMessage}',
  },
  fileNameTooLongNotice: {
    id: 'support_conversations_client.attachments.fileNameTooLongNotice',
    description:
      'Message indicating that the file cannot be uploaded because the filename is too long.',
    defaultMessage: 'File name must be 200 characters or less',
  },
});

export const supportedMimeTypes: string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'text/plain',
  'text/xml',
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_FILE_NAME_LENGTH = 200;

const getMessageForUploadError = (error: any) => {
  const errorCode = error?.code || null;

  switch (errorCode) {
    case 'file_too_large':
      return messages.fileTooBigNotice;
    case 'upload_type_not_supported':
      return messages.fileTypeNotSupportedNotice;
    case 'upload_ext_invalid':
      return messages.fileInvalidNotice;
    case 'upload_not_supported':
      return messages.fileInvalidNotice;
    case 'too_many_files':
      return messages.tooManyFilesNotice;
    case 'file_name_too_long':
      return messages.fileNameTooLongNotice;
    default:
      return messages.unexpectedErrorNotice;
  }
};

class ValidationError extends Error {
  file?: File;

  code?: string;

  constructor({
    message,
    file,
    code,
  }: {
    message: string;
    file?: File;
    code?: string;
  }) {
    super(message);
    this.file = file;
    this.code = code;
  }
}

const validateFile = (file: File) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new ValidationError({
      message: 'File is too large',
      file,
      code: 'file_too_large',
    });
  }

  if (!supportedMimeTypes.includes(file.type)) {
    throw new ValidationError({
      message: 'File type is not supported',
      file,
      code: 'upload_type_not_supported',
    });
  }

  if (file.name.length > MAX_FILE_NAME_LENGTH) {
    throw new ValidationError({
      message: 'File name is too long',
      file,
      code: 'file_name_too_long',
    });
  }
};

const uploadFile = async ({
  uploadsHost,
  apiKey,
  merchantId,
  livemode,
  file,
  abortSignal,
}: {
  uploadsHost: string;
  apiKey: string;
  merchantId: string;
  livemode?: boolean;
  file: File;
  abortSignal?: AbortSignal;
}): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('purpose', 'support_attachment');

  const headers: Record<string, string> = {
    Authorization: `Bearer ${apiKey}`,
    'Stripe-Account': merchantId,
  };

  if (livemode != null) {
    headers['Stripe-Livemode'] = livemode ? 'true' : 'false';
  }

  // See: https://stripe.com/docs/api/files
  const response = await fetch(`${uploadsHost}/v1/files`, {
    method: 'POST',
    headers,
    body: formData,
    signal: abortSignal,
  });

  if (!response.ok) {
    const data = await response.json();
    const message = data?.error?.message || 'Failed to upload file';
    const code = data?.error?.message_code;
    throw new ValidationError({message, file, code});
  }

  const data = await response.json();
  return data.id;
};

type Props = {
  attachments: Attachment[];
  remainingAttachmentCount: number;
  onAttachmentsAdded: (attachments: Attachment[]) => void;
  onRemoveAttachment: (attachment: Attachment) => void;
  onDismiss: () => void;
};

export function AttachmentUploader({
  attachments,
  remainingAttachmentCount,
  onAttachmentsAdded,
  onRemoveAttachment,
  onDismiss,
}: Props) {
  const {
    urls: {uploadsHost},
  } = useContext(ConfigContext);
  const {profile} = useProfile();
  const {visitor, merchant} = profile;
  const analytics = useClientAnalytics();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadBatchTotal, setUploadBatchTotal] = useState<number>(1);
  const [uploadBatchCurrentIndex, setUploadBatchCurrentIndex] =
    useState<number>(0);

  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const [abortController, setAbortController] = useState<AbortController>(
    new AbortController(),
  );

  const handleCancel = useCallback(() => {
    abortController.abort();
    onDismiss();
  }, [abortController, onDismiss]);

  const processFile = useCallback(
    async (file: File): Promise<Attachment | null> => {
      // Currently only logged in users can upload attachments
      if (!visitor.sessionApiKey || !merchant?.id) {
        return null;
      }

      const requestAbortController = new AbortController();
      setAbortController(requestAbortController);

      const fileId = await uploadFile({
        uploadsHost,
        apiKey: visitor.sessionApiKey,
        merchantId: merchant.id,
        livemode: profile.livemode,
        file,
        abortSignal: requestAbortController.signal,
      });

      return {
        id: fileId,
        fileName: file.name,
        mimeType: file.type,
        file,
      };
    },
    [visitor, merchant, uploadsHost, profile],
  );

  const handleFilesSelected = useCallback(
    async (files: FileList) => {
      setUploadBatchTotal(files.length);
      setUploadBatchCurrentIndex(0);
      setIsLoading(true);
      setErrorMessage(null);

      try {
        if (files.length > remainingAttachmentCount) {
          throw new ValidationError({
            message: 'Too many files',
            code: 'too_many_files',
          });
        }

        for (let i = 0; i < files.length; i += 1) {
          setUploadBatchCurrentIndex(i);
          const file = files[i];
          validateFile(file);

          // Upload attachments sequentially
          // eslint-disable-next-line no-await-in-loop
          const attachment = await processFile(file);
          if (attachment) {
            analytics.action(
              'support_conversations_attachments_upload_success',
              {
                mimeType: file.type,
              },
            );
            onAttachmentsAdded([attachment]);
          }
        }

        onDismiss();
      } catch (error: any) {
        analytics.action('support_conversations_attachments_upload_error', {
          errorCode: error?.code || '',
          mimeType: error?.file?.type || '',
        });
        const message = (
          <FormattedMessage {...getMessageForUploadError(error)} />
        );

        if (files.length > 1 && error?.code !== 'too_many_files') {
          setErrorMessage(
            <FormattedMessage
              {...messages.batchUploadErrorNotice}
              values={{errorMessage: message}}
            />,
          );
        } else {
          setErrorMessage(message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      remainingAttachmentCount,
      analytics,
      processFile,
      onAttachmentsAdded,
      onDismiss,
    ],
  );

  return (
    <view.form
      onSubmit={(e) => e.preventDefault()}
      data-testid="support-add-attachment-form"
      css={{position: css.value('relative')}}
    >
      <UploadFileControl
        attachments={attachments}
        supportedMimeTypes={supportedMimeTypes}
        isLoading={isLoading}
        uploadBatchTotal={uploadBatchTotal}
        uploadBatchCurrentIndex={uploadBatchCurrentIndex}
        error={errorMessage}
        onFilesSelected={handleFilesSelected}
        onRemoveAttachment={onRemoveAttachment}
      />
      <CloseButton onClick={handleCancel} />
    </view.form>
  );
}
